'use client';

import { useCallback, useEffect, useRef, type KeyboardEvent, type MouseEvent } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useCartStore, useShallow } from './store/CartStoreContext';
import { CartEmptyState } from './components';
import { ErrorBoundary } from '../ErrorBoundary';
import { RouteIntlScope } from 'lib/i18n/routeIntl';
import { useFocusTrap } from 'hooks/dom/useFocusTrap';
import { useLastActivatedElement } from 'hooks/dom/useLastActivatedElement';
import styles from './index.module.css';

function CartLoadingStatus() {
  const t = useTranslations('common');
  return (
    <div
      role="status"
      aria-label={t('cart.content.ariaLabel.loadingCart')}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 240,
        padding: 24,
      }}
    />
  );
}

const CartContent = dynamic(
  () => import('./Cart').then((m) => m.CartContent),
  {
    ssr: false,
    loading: () => <CartLoadingStatus />,
  },
);

// The panel takes focus only when we put it there (pointer open, empty cart).
// tabindex is added for that moment and dropped on blur, so a later click on
// blank space inside the cart does not focus the panel (which would also
// stop arrow keys scrolling the item list).
const asFocusablePanel = (panel: HTMLElement): HTMLElement => {
  if (!panel.hasAttribute('tabindex')) {
    panel.setAttribute('tabindex', '-1');
    panel.addEventListener('blur', () => panel.removeAttribute('tabindex'), {
      once: true,
    });
  }
  return panel;
};

// Last match wins: when altContent is open its "×" sits on top of the
// header's close button. Falls back to the panel itself (empty cart).
const getCloseButton = (root: HTMLElement): HTMLElement =>
  Array.from(root.querySelectorAll<HTMLElement>('[data-cart-close]')).pop() ??
  asFocusablePanel(root);

// FIXME: Simplify cart state/validation by normalizing around Shopify cart responses and reducing duplicate client-side checks.


export const Cart = () => {
  const { cart, isCartVisible, altContent, hideCart } = useCartStore(
    useShallow((s) => ({
      cart: s.cart,
      isCartVisible: s.isCartVisible,
      altContent: s.altContent,
      hideCart: s.hideCart,
    })),
  );

  const tCart = useTranslations('cart');
  const panelRef = useRef<HTMLDivElement>(null);

  // Closed state is `display: none` (index.module.css), which already removes
  // the cart from the tab order and the accessibility tree, so no aria-hidden.
  // Escape is handled on the panel rather than by the trap, so it only fires
  // while focus is inside the cart, yields to controls that consume it (the
  // quantity listbox calls preventDefault) and to modals nested in the cart.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // The cart's InfoModal closes itself on Escape (InfoModal.tsx).
      if (panelRef.current?.querySelector('[data-cart-info-modal]')) return;
      const target = event.target as HTMLElement;
      if (target.closest('[aria-modal="true"]') !== panelRef.current) return;
      event.stopPropagation();
      closedByKeyboardRef.current = true;
      hideCart();
    },
    [hideCart],
  );

  // The trap restores focus to whatever was focused when the cart opened, but
  // "Add to cart" is disabled while its request runs, so focus has already
  // dropped to <body> by then. Fall back to the last button or link activated
  // outside the cart (never an input, so no stray caret on a mouse close).
  // Keyboard users only: Safari does not focus clicked buttons, so for mouse
  // users focus stays where the browser leaves it (a stray Space must not
  // re-add a ring or reopen the cart).
  const lastActivationRef = useLastActivatedElement(panelRef);
  // Set by an Escape or a keyboard-activated Close (any [data-cart-close],
  // the header's included); a pointer close leaves focus where the browser
  // left it, as before. Cleared on every open and every read.
  const closedByKeyboardRef = useRef(false);
  useEffect(() => {
    if (isCartVisible) closedByKeyboardRef.current = false;
  }, [isCartVisible]);
  const noteCloseActivation = useCallback((event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-cart-close]')) {
      closedByKeyboardRef.current = event.detail === 0;
    }
  }, []);

  useFocusTrap(panelRef, {
    active: isCartVisible,
    // Keyboard open: start on Close. Pointer open: the panel itself, so the
    // dialog still holds focus but Space / Enter do nothing.
    initialFocus: (root) =>
      lastActivationRef.current.keyboard
        ? getCloseButton(root)
        : asFocusablePanel(root),
    restoreFocus: true,
    contain: true,
    // No keyboard opener (the drawer auto-opens after add-to-cart): after a
    // keyboard close, fall back to the header cart icon (data-cart-trigger in
    // Header/Cart.tsx) so the keyboard user is not dropped on <body>.
    restoreFallback: () => {
      const byKeyboard = closedByKeyboardRef.current;
      closedByKeyboardRef.current = false;
      if (lastActivationRef.current.keyboard) {
        return lastActivationRef.current.element;
      }
      return byKeyboard
        ? document.querySelector<HTMLElement>('[data-cart-trigger]')
        : null;
    },
  });

  // Cart content is loaded lazily, so on a keyboard open the close button may
  // not exist yet when the trap sets initial focus and focus lands on the
  // panel. Move it to the close button once that mounts, unless the user has
  // moved focus or clicked inside the cart.
  useEffect(() => {
    const panel = panelRef.current;
    if (!isCartVisible || !panel || !lastActivationRef.current.keyboard) {
      return;
    }
    const stop = () => {
      observer.disconnect();
      panel.removeEventListener('pointerdown', stop);
    };
    const observer = new MutationObserver(() => {
      if (document.activeElement !== panel) return;
      const close = getCloseButton(panel);
      if (close === panel) return;
      close.focus();
      stop();
    });
    observer.observe(panel, { childList: true, subtree: true });
    panel.addEventListener('pointerdown', stop);
    const timeout = window.setTimeout(stop, 2000);
    return () => {
      stop();
      window.clearTimeout(timeout);
    };
  }, [isCartVisible, lastActivationRef]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click is a pointer shortcut; keyboard users close via Escape or the close button
    <div
      className={`${styles.root} ${isCartVisible ? 'active' : ''}`}
      onClick={hideCart}
      data-testid="cart"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog panel: stops backdrop clicks propagating and handles Escape */}
      <div
        ref={panelRef}
        className={styles.content}
        role="dialog"
        aria-modal="true"
        aria-label={tCart('sharedCart.dialogLabel')}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onClickCapture={noteCloseActivation}
        onKeyDown={handleKeyDown}
      >
        {cart &&
          cart?.lines &&
          cart?.lines.edges &&
          cart?.lines.edges.length >= 1 && (
            <ErrorBoundary
              name="cart-content"
              // Reset error state when line count changes (avoid sticky fallback after transient error).
              resetKeys={[cart.lines.edges.length]}
              fallback={<CartEmptyState onBackToBrowse={hideCart} />}
            >
              <CartContent />
            </ErrorBoundary>
          )}

        {(!cart ||
          !cart?.lines ||
          !cart?.lines.edges ||
          !(cart?.lines.edges.length >= 1)) && (
          <CartEmptyState onBackToBrowse={hideCart} />
        )}

        {altContent && (
          <div className={styles.overlayContent}>
            <h2>
              &nbsp;
              <button
                className={styles.closeBtn}
                onClick={hideCart ?? null}
                aria-label={tCart('sharedCart.closeCartAria')}
                data-cart-close
              >
                <span aria-hidden="true">×</span>
              </button>
            </h2>
            <RouteIntlScope>{altContent}</RouteIntlScope>
          </div>
        )}
      </div>
    </div>
  );
};

export * from './context';
export { getShippingAndTaxDisclaimerMap } from './helper';
